import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePadelCenter } from "@/context/PadelCenterContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, Coin, Sword, Users, Buildings, ShieldCheck, Eye, GearSix, UserCircle, Key, ArrowsClockwise, CopySimple, Lock, CheckCircle } from "@phosphor-icons/react";
import PadelCenterLogo, { PadelCenterBadge } from "@/components/PadelCenterLogo";

const SUPER_ADMIN_EMAILS = [
  "gardunoalboreshectorjesus@gmail.com",
  "disfrutaydivierteteoficial@gmail.com",
];

function JoinGate({ onJoined }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) { toast.error("Ingresa el código"); return; }
    setLoading(true);
    try {
      await api.post("/padel-center/join", { code: code.trim() });
      toast.success("¡Bienvenido a Padel Center!");
      onJoined?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código inválido");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="pc-join-gate">
      <div className="max-w-md w-full bg-gradient-to-br from-[#0a1a0a] via-black to-[#0a0a0a] border-2 border-[#CCFF00]/40 rounded-xl p-8 shadow-2xl">
        <div className="flex flex-col items-center text-center mb-6">
          <PadelCenterLogo size={140} withText={false} glow />
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mt-4">PADEL CENTER</h1>
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#CCFF00]/80 font-bold">CLUB DE CAMPEONES</p>
          <p className="text-xs text-gray-400 mt-3 italic">Ecosistema privado · acceso por invitación</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-[10px] tracking-[0.3em] uppercase font-bold text-gray-400 block">Código de acceso</label>
          <Input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="PadelCenter#..."
            autoFocus
            className="bg-black border-[#CCFF00]/30 text-white text-center tracking-wider"
            data-testid="pc-join-code-input"
          />
          <Button type="submit" disabled={loading} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="pc-join-submit">
            {loading ? "Validando..." : "Entrar al ecosistema"}
          </Button>
        </form>
        <p className="text-[10px] text-gray-500 text-center mt-4">¿No tienes el código? Solicítalo al director de tu sede.</p>
      </div>
    </div>
  );
}

function Welcome({ clubs, visibility, onVisibilityChange, sede, onSedeChange }) {
  return (
    <div className="card-tactical p-5 bg-gradient-to-br from-[#0a1a0a]/80 to-black border-[#CCFF00]/30" data-testid="pc-welcome">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <PadelCenterLogo size={80} />
        <div className="flex flex-col gap-3 min-w-[260px]">
          <div>
            <label className="text-[9px] tracking-[0.3em] uppercase font-bold text-gray-400">Visibilidad de mis marcadores</label>
            <Select value={visibility} onValueChange={onVisibilityChange}>
              <SelectTrigger className="bg-black border-[#CCFF00]/30 text-xs" data-testid="pc-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">🌐 Global + Padel Center (ambos)</SelectItem>
                <SelectItem value="padel_center">🔒 Solo Padel Center</SelectItem>
                <SelectItem value="global">🌍 Solo Global (público)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[9px] tracking-[0.3em] uppercase font-bold text-gray-400">Mi sede principal</label>
            <Select value={sede || "__none__"} onValueChange={(v) => onSedeChange(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-black border-[#CCFF00]/30 text-xs" data-testid="pc-sede-select">
                <SelectValue placeholder="Selecciona sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin sede asignada —</SelectItem>
                <SelectItem value="condado">🏢 Condado del Valle</SelectItem>
                <SelectItem value="townsquare">🏢 Townsquare</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {clubs?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {clubs.map((c) => (
            <Badge key={c.id} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00]/80 text-[10px] uppercase tracking-wider">
              <Buildings size={10} className="mr-1" /> {c.name}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function RankingTab() {
  const [sedeFilter, setSedeFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const url = sedeFilter === "all" ? "/padel-center/ranking" : `/padel-center/ranking?sede=${sedeFilter}`;
    api.get(url).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, [sedeFilter]);
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" data-testid="pc-ranking-sede-filter">
        {[
          { v: "all", label: "🌐 Todas las sedes" },
          { v: "condado", label: "🏢 Condado del Valle" },
          { v: "townsquare", label: "🏢 Townsquare" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setSedeFilter(opt.v)}
            className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded border transition-colors ${
              sedeFilter === opt.v
                ? "bg-[#CCFF00]/15 border-[#CCFF00] text-[#CCFF00]"
                : "bg-white/[0.03] border-white/15 text-gray-400 hover:border-[#CCFF00]/30"
            }`}
            data-testid={`pc-ranking-sede-${opt.v}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>
      ) : !rows.length ? (
        <div className="text-center text-gray-500 text-sm py-8" data-testid="pc-ranking-empty">Sin estadísticas{sedeFilter !== "all" ? ` en ${sedeFilter === "condado" ? "Condado del Valle" : "Townsquare"}` : ""} aún.</div>
      ) : (
        <div className="space-y-1.5" data-testid="pc-ranking-list">
          {rows.map((p, i) => {
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
            const bg = i === 0 ? "bg-amber-400/20 border-amber-400/40" : i === 1 ? "bg-gray-400/20 border-gray-400/40" : i === 2 ? "bg-orange-700/20 border-orange-700/40" : "bg-white/[0.03] border-white/10";
            return (
              <div key={p.user_id} className={`flex items-center justify-between px-3 py-2 border rounded ${bg}`} data-testid={`pc-ranking-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-sm font-mono w-7 text-center shrink-0">{medal || `#${i + 1}`}</span>
                  <span className="text-sm text-white truncate font-medium">{p.name}</span>
                  {p.sede && <Badge variant="outline" className="border-white/15 text-gray-400 text-[9px] uppercase shrink-0">{p.sede === "condado" ? "Condado" : "Townsquare"}</Badge>}
                  {p.role_in_pc === "directivo" && <Badge variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] text-[9px] uppercase shrink-0">Directivo</Badge>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[9px] text-gray-500 uppercase">{p.games_won}V · {p.games_played}j</span>
                  <span className="font-['Barlow_Condensed'] text-xl font-black text-[#CCFF00] leading-none">{p.points}</span>
                  <span className="text-[9px] text-gray-500 uppercase">pts</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DydRankingTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/padel-center/dyd-ranking").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>;
  if (!rows.length) return <div className="text-center text-gray-500 text-sm py-8">Sin movimientos DYD aún.</div>;
  return (
    <div className="space-y-1.5" data-testid="pc-dyd-list">
      {rows.map((p, i) => (
        <div key={p.user_id} className="flex items-center justify-between px-3 py-2 border border-white/10 rounded bg-white/[0.03]" data-testid={`pc-dyd-row-${i}`}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-sm font-mono w-7 text-center text-gray-400 shrink-0">#{i + 1}</span>
            <span className="text-sm text-white truncate">{p.name}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Coin size={14} weight="fill" className="text-amber-400" />
            <span className="font-['Barlow_Condensed'] text-xl font-black text-amber-400 leading-none">{p.balance}</span>
            <span className="text-[9px] text-gray-500 uppercase">DYD</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RetasTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/padel-center/retas").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>;
  if (!rows.length) return (
    <div className="text-center text-gray-500 text-sm py-8" data-testid="pc-retas-empty">
      <Sword size={32} weight="duotone" className="mx-auto mb-2 text-gray-700" />
      Sin retas registradas en Padel Center.
      <div className="mt-3"><Button size="sm" onClick={() => navigate("/dashboard/retas")} className="bg-[#CCFF00] text-black text-xs uppercase font-bold">Ir a Retas</Button></div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="pc-retas-list">
      {rows.map((r) => (
        <div key={r.id} className="card-tactical p-3 cursor-pointer hover:border-[#CCFF00]/40" onClick={() => navigate("/dashboard/retas")} data-testid={`pc-reta-${r.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-bold text-white">{r.date} · {r.time || ""}</div>
            <Badge variant="outline" className="text-[10px]">{r.status || "abierto"}</Badge>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">{r.description || "Sin descripción"}</div>
        </div>
      ))}
    </div>
  );
}

function TournamentsTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/padel-center/tournaments").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>;
  if (!rows.length) return (
    <div className="text-center text-gray-500 text-sm py-8" data-testid="pc-tournaments-empty">
      <Trophy size={32} weight="duotone" className="mx-auto mb-2 text-gray-700" />
      Sin torneos registrados en Padel Center.
      <div className="mt-3"><Button size="sm" onClick={() => navigate("/dashboard/torneos")} className="bg-[#CCFF00] text-black text-xs uppercase font-bold">Ir a Torneos</Button></div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="pc-tournaments-list">
      {rows.map((t) => (
        <div key={t.id} className="card-tactical p-3 cursor-pointer hover:border-[#CCFF00]/40" onClick={() => navigate("/dashboard/torneos")} data-testid={`pc-tournament-${t.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-bold text-white">{t.name}</div>
            <Badge variant="outline" className="text-[10px]">{t.status || "open"}</Badge>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">{t.start_date || ""} · {t.category || ""}</div>
        </div>
      ))}
    </div>
  );
}

function MembersTab({ isAdmin, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const load = useCallback(() => {
    setLoading(true);
    const url = isAdmin ? "/padel-center/admin/members" : "/padel-center/members";
    api.get(url).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role, name) => {
    try {
      await api.put(`/padel-center/admin/members/${uid}/role`, { role });
      toast.success(`✓ ${name} ahora es ${role === "directivo" ? "Directivo" : "Jugador"}`, { description: "Rol actualizado correctamente." });
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const setMemberSede = async (uid, sede, name) => {
    try {
      await api.put(`/padel-center/admin/members/${uid}/sede`, { sede });
      const sedeLabel = sede === "condado" ? "Condado del Valle" : sede === "townsquare" ? "Townsquare" : "Sin sede";
      toast.success(`✓ ${name} asignado a ${sedeLabel}`, { description: "La sede fue actualizada en el ecosistema." });
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const setStatus = async (uid, status, name) => {
    try {
      await api.put(`/padel-center/admin/members/${uid}/status`, { status });
      const label = status === "approved" ? "APROBADO" : status === "rejected" ? "RECHAZADO" : "PENDIENTE";
      toast.success(`✓ ${name} → ${label}`, { description: status === "approved" ? "El miembro ya tiene acceso completo al ecosistema." : status === "rejected" ? "El miembro no podrá acceder al contenido PC." : "El miembro queda en revisión." });
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const remove = async (uid, name) => {
    if (!window.confirm(`Expulsar a ${name} de Padel Center?`)) return;
    try {
      await api.delete(`/padel-center/admin/members/${uid}`);
      toast.success(`✓ ${name} fue removido del ecosistema`);
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>;
  if (!rows.length) return <div className="text-center text-gray-500 text-sm py-8">Sin miembros aún.</div>;

  // Compute pending list (admin) for the highlighted top section
  const pendingRows = isAdmin ? rows.filter((m) => (m.padel_center_status || "approved") === "pending") : [];
  const filteredRows = isAdmin && filter !== "all"
    ? rows.filter((m) => (m.padel_center_status || "approved") === filter)
    : rows;

  const renderRow = (m, isPendingSection = false) => {
    const status = m.padel_center_status || "approved";
    const statusBadge = status === "pending"
      ? <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[9px] uppercase shrink-0 animate-pulse">Pendiente</Badge>
      : status === "rejected"
      ? <Badge variant="outline" className="border-red-500/40 text-red-300 text-[9px] uppercase shrink-0">Rechazado</Badge>
      : <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[9px] uppercase shrink-0">Aprobado</Badge>;
    const rowBg = isPendingSection
      ? "bg-amber-400/8 border-amber-400/30"
      : status === "rejected"
      ? "bg-red-500/5 border-red-500/20 opacity-60"
      : "bg-white/[0.03] border-white/10";
    return (
      <div key={m.id} className={`flex items-center justify-between px-3 py-2 border rounded ${rowBg}`} data-testid={`pc-member-${m.id}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <UserCircle size={28} weight="duotone" className="text-gray-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm text-white truncate font-medium">{m.name}</div>
            <div className="text-[10px] text-gray-500 truncate">{m.email || m.phone || ""}</div>
          </div>
          {statusBadge}
          {m.padel_center_role === "directivo" && <Badge variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] text-[9px] uppercase shrink-0">Directivo</Badge>}
          {m.padel_center_sede && (
            <Badge variant="outline" className="border-white/15 text-gray-400 text-[9px] uppercase shrink-0" title="Sede">
              <Buildings size={9} className="mr-1" />{m.padel_center_sede === "condado" ? "Condado" : "Townsquare"}
            </Badge>
          )}
          {m.padel_center_visibility && (
            <Badge variant="outline" className="border-white/15 text-gray-400 text-[9px] uppercase shrink-0" title="Visibilidad">
              <Eye size={9} className="mr-1" />{m.padel_center_visibility === "both" ? "Ambos" : m.padel_center_visibility === "padel_center" ? "Solo PC" : "Global"}
            </Badge>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-1 shrink-0 items-center">
            {status !== "approved" && (
              <Button size="sm" onClick={() => setStatus(m.id, "approved", m.name)} className="text-[10px] h-7 bg-emerald-500 hover:bg-emerald-400 text-black uppercase font-bold" data-testid={`pc-approve-${m.id}`}>
                <CheckCircle size={12} className="mr-1" />Aprobar
              </Button>
            )}
            {status !== "rejected" && (
              <Button size="sm" variant="outline" onClick={() => setStatus(m.id, "rejected", m.name)} className="text-[10px] h-7 border-red-500/30 text-red-400 uppercase" data-testid={`pc-reject-${m.id}`}>
                Rechazar
              </Button>
            )}
            <Select value={m.padel_center_sede || "__none__"} onValueChange={(v) => setMemberSede(m.id, v === "__none__" ? "" : v, m.name)}>
              <SelectTrigger className="bg-black border-white/15 h-7 text-[10px] w-32" data-testid={`pc-member-sede-select-${m.id}`}>
                <SelectValue placeholder="Asignar sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin sede —</SelectItem>
                <SelectItem value="condado">🏢 Condado</SelectItem>
                <SelectItem value="townsquare">🏢 Townsquare</SelectItem>
              </SelectContent>
            </Select>
            {m.padel_center_role === "directivo" ? (
              <Button size="sm" variant="outline" onClick={() => setRole(m.id, "jugador", m.name)} className="text-[10px] h-7 border-white/15 uppercase" data-testid={`pc-demote-${m.id}`}>
                Quitar dir.
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setRole(m.id, "directivo", m.name)} className="text-[10px] h-7 border-[#CCFF00]/30 text-[#CCFF00] uppercase" data-testid={`pc-promote-${m.id}`}>
                Hacer dir.
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => remove(m.id, m.name)} className="text-[10px] h-7 border-red-500/30 text-red-400 uppercase" data-testid={`pc-remove-${m.id}`}>
              Expulsar
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="pc-members-list">
      {/* Pending section (admin) — highlighted on top */}
      {isAdmin && pendingRows.length > 0 && (
        <div className="card-tactical p-4 border-amber-400/40 bg-amber-500/5" data-testid="pc-pending-section">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={16} weight="duotone" className="text-amber-400" />
            <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-amber-300">Pendientes de aprobación · {pendingRows.length}</span>
          </div>
          <div className="space-y-1.5">{pendingRows.map((m) => renderRow(m, true))}</div>
        </div>
      )}

      {/* Filter buttons (admin) */}
      {isAdmin && (
        <div className="flex flex-wrap gap-2" data-testid="pc-members-filter">
          {[
            { v: "all", label: `Todos · ${rows.length}` },
            { v: "approved", label: `Aprobados · ${rows.filter((m) => (m.padel_center_status || "approved") === "approved").length}` },
            { v: "pending", label: `Pendientes · ${pendingRows.length}` },
            { v: "rejected", label: `Rechazados · ${rows.filter((m) => m.padel_center_status === "rejected").length}` },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilter(opt.v)}
              className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded border transition-colors ${
                filter === opt.v
                  ? "bg-[#CCFF00]/15 border-[#CCFF00] text-[#CCFF00]"
                  : "bg-white/[0.03] border-white/15 text-gray-400 hover:border-[#CCFF00]/30"
              }`}
              data-testid={`pc-members-filter-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        {filteredRows.map((m) => renderRow(m, false))}
      </div>
    </div>
  );
}

function CodeAdminTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [custom, setCustom] = useState("");
  const [reveal, setReveal] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/padel-center/admin/code").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Acceso restringido")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const rotate = async () => {
    if (!window.confirm("¿Generar un código nuevo? El código anterior dejará de funcionar.")) return;
    setRotating(true);
    try {
      const r = await api.post("/padel-center/admin/code/rotate");
      toast.success(`Nuevo código generado: ${r.data.code}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setRotating(false); }
  };
  const setCustomCode = async () => {
    if (!custom.trim() || custom.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    try {
      await api.put("/padel-center/admin/code", { code: custom.trim() });
      toast.success("Código actualizado");
      setCustom("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const copy = () => {
    if (!data?.code) return;
    try { navigator.clipboard.writeText(data.code); toast.success("Copiado al portapapeles"); }
    catch { /* ignore */ }
  };
  if (loading) return <div className="text-center text-gray-500 text-sm py-8">Cargando...</div>;
  if (!data) return <div className="text-center text-red-400 text-sm py-8">Acceso restringido al creador del ecosistema.</div>;
  return (
    <div className="space-y-4" data-testid="pc-admin-code">
      <div className="card-tactical p-5 border-[#CCFF00]/40 bg-gradient-to-br from-[#0a1a0a]/60 to-black">
        <div className="flex items-center gap-2 mb-3">
          <Key size={16} weight="fill" className="text-[#CCFF00]" />
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Código de acceso actual</span>
          <Badge variant="outline" className="ml-auto border-amber-400/40 text-amber-300 text-[9px] uppercase">Super-admin</Badge>
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex-1 min-w-[200px] bg-black border border-[#CCFF00]/30 rounded px-3 py-2 font-mono text-base tracking-wider text-white" data-testid="pc-code-display">
            {reveal ? data.code : "•".repeat(Math.min(20, (data.code || "").length))}
          </div>
          <Button size="sm" variant="outline" onClick={() => setReveal(!reveal)} className="border-white/15" data-testid="pc-code-reveal">
            <Eye size={14} className="mr-1" /> {reveal ? "Ocultar" : "Mostrar"}
          </Button>
          <Button size="sm" variant="outline" onClick={copy} className="border-white/15" data-testid="pc-code-copy">
            <CopySimple size={14} className="mr-1" /> Copiar
          </Button>
        </div>
        {data.updated_at && (
          <p className="text-[10px] text-gray-500 mb-3">Última actualización: {new Date(data.updated_at).toLocaleString()} {data.updated_by_name ? `· por ${data.updated_by_name}` : ""}</p>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={rotate} disabled={rotating} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="pc-code-rotate">
            <ArrowsClockwise size={14} className="mr-1" /> {rotating ? "Generando..." : "Generar nuevo código aleatorio"}
          </Button>
          <span className="text-[10px] text-gray-500">Notifica a los admins automáticamente</span>
        </div>
        <div className="mt-4 pt-3 border-t border-white/5 flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[9px] tracking-[0.25em] uppercase font-bold text-gray-400 block mb-1">Establecer código personalizado</label>
            <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Mínimo 6 caracteres" className="bg-black border-white/15 text-white" data-testid="pc-code-custom-input" maxLength={60} />
          </div>
          <Button onClick={setCustomCode} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] uppercase font-bold text-xs" data-testid="pc-code-custom-set">Aplicar</Button>
        </div>
      </div>

      {data.history?.length > 0 && (
        <div className="card-tactical p-4" data-testid="pc-code-history">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-gray-400 mb-2">Historial (últimos 20 cambios)</div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {[...data.history].reverse().map((h, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[10px] py-1.5 border-b border-white/5 last:border-0">
                <span className="font-mono text-gray-300 flex-1 truncate">{h.code}</span>
                <span className="text-gray-500 shrink-0">{new Date(h.set_at).toLocaleString()}</span>
                <span className="text-gray-500 shrink-0 truncate max-w-[120px]">· {h.set_by_name || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminSetup({ onSeeded }) {
  const [loading, setLoading] = useState(false);
  const [syncingPlayers, setSyncingPlayers] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/padel-center/admin/seed-clubs");
      toast.success(`✓ Clubes sincronizados`, { description: `${r.data.updated_clubs} actualizados, ${r.data.created_clubs || 0} creados.` });
      onSeeded?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  const syncPlayers = async () => {
    setSyncingPlayers(true);
    try {
      const r = await api.post("/padel-center/admin/sync-players");
      toast.success("✓ Jugadores sincronizados", {
        description: `${r.data.members_total} miembros · ${r.data.members_fixed} corregidos · ${r.data.legacy_approved} legacy auto-aprobados.`,
      });
      onSeeded?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSyncingPlayers(false); }
  };
  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const r = await api.post("/padel-center/admin/test-pending-email");
      if (r.data.ok) {
        toast.success("✓ Email de prueba enviado", { description: `Revisa la bandeja de los super-admins. Usuario usado: ${r.data.member_used}.` });
      } else {
        toast.error("Email no pudo enviarse", { description: "Revisa la configuración de SendGrid." });
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setTestingEmail(false); }
  };
  return (
    <div className="card-tactical p-4 border-amber-400/30 bg-amber-500/5" data-testid="pc-admin-setup">
      <div className="flex items-center gap-2 mb-2">
        <GearSix size={16} className="text-amber-400" />
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-300">Admin · Sincronización & Notificaciones</span>
      </div>
      <p className="text-xs text-gray-400 mb-3">Sincroniza clubes/jugadores. Cuando llega un miembro pendiente, los super-admins reciben automáticamente un email con botones para aprobar y compartir por WhatsApp.</p>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={run} disabled={loading} className="bg-amber-400 text-black hover:bg-amber-300 font-bold uppercase text-xs" data-testid="pc-seed-btn">
          <Buildings size={14} className="mr-1" /> {loading ? "Sincronizando..." : "Sincronizar clubes"}
        </Button>
        <Button onClick={syncPlayers} disabled={syncingPlayers} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase text-xs" data-testid="pc-sync-players-btn">
          <Users size={14} className="mr-1" /> {syncingPlayers ? "Sincronizando..." : "Sincronizar jugadores"}
        </Button>
        <Button onClick={testEmail} disabled={testingEmail} variant="outline" className="border-blue-400/40 text-blue-300 hover:bg-blue-400/10 font-bold uppercase text-xs" data-testid="pc-test-email-btn">
          📧 {testingEmail ? "Enviando..." : "Probar email pendiente"}
        </Button>
      </div>
    </div>
  );
}

function MyProfileTab({ pc, onVisibilityChange, onSedeChange }) {
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    api.get("/padel-center/visibility-impact").then((r) => setImpact(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-4" data-testid="pc-my-profile-tab">
      <div className="card-tactical p-5 bg-gradient-to-br from-[#0a1a0a]/80 to-black border-[#CCFF00]/30">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <PadelCenterLogo size={80} />
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-wider">
            <ShieldCheck size={10} className="mr-1" /> Acceso aprobado
          </Badge>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase font-bold text-gray-400 block mb-1">Mi sede principal</label>
            <Select value={pc.sede || "__none__"} onValueChange={(v) => onSedeChange(v === "__none__" ? "" : v)}>
              <SelectTrigger className="bg-black border-[#CCFF00]/30 text-sm" data-testid="pc-sede-select">
                <SelectValue placeholder="Selecciona sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin sede asignada —</SelectItem>
                <SelectItem value="condado">🏢 Condado del Valle</SelectItem>
                <SelectItem value="townsquare">🏢 Townsquare</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-gray-500 mt-1">Define el ranking por sede al que perteneces.</p>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase font-bold text-gray-400 block mb-1">Visibilidad de mis marcadores</label>
            <Select value={pc.visibility || "both"} onValueChange={onVisibilityChange}>
              <SelectTrigger className="bg-black border-[#CCFF00]/30 text-sm" data-testid="pc-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">🌐 Global + Padel Center (ambos)</SelectItem>
                <SelectItem value="padel_center">🔒 Solo Padel Center (oculto del público)</SelectItem>
                <SelectItem value="global">🌍 Global (público)</SelectItem>
              </SelectContent>
            </Select>
            {impact && (
              <p className="text-[10px] mt-1 leading-snug" data-testid="pc-visibility-impact">
                {pc.visibility === "padel_center" ? (
                  <span className="text-amber-300">🔒 <strong>{impact.options.padel_center.hidden_from}</strong> personas del público no podrán verte</span>
                ) : (
                  <span className="text-gray-400">🌐 Te ven <strong>{impact.total_non_pc_audience}</strong> personas del público</span>
                )}
              </p>
            )}
          </div>
        </div>
        {pc.clubs?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-white/5">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1">Mis sedes:</span>
            {pc.clubs.map((c) => (
              <Badge key={c.id} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00]/80 text-[10px] uppercase tracking-wider">
                <Buildings size={10} className="mr-1" /> {c.name}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="text-center text-[11px] text-gray-500 italic py-2">
        💡 Solo directivos y administradores tienen acceso al ranking general, lista de miembros y panel del ecosistema. Para más información, contacta al directivo de tu sede.
      </div>
    </div>
  );
}

function AdminLogoUploader({ onSaved }) {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState("");
  const fileRef = useRef(null);
  useEffect(() => {
    api.get("/padel-center/logo").then((r) => setCurrentLogo(r.data?.url || "")).catch(() => {});
  }, []);
  const uploadFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) { toast.error("Solo JPEG, PNG o WEBP"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "logo.jpg");
      const upRes = await api.post("/padel-center/admin/logo/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedUrl = upRes.data?.url;
      if (!uploadedUrl) {
        toast.error("Upload no devolvió URL");
        return;
      }
      toast.success("✓ Logo actualizado", { description: "Se ve en toda la plataforma PC." });
      setCurrentLogo(uploadedUrl);
      try {
        const m = await import("@/components/PadelCenterLogo");
        m.invalidatePCLogoCache?.();
      } catch { /* ignore */ }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };
  const setExistingUrl = async () => {
    if (!url.trim()) return;
    try {
      await api.put("/padel-center/admin/logo", { url: url.trim() });
      toast.success("✓ Logo URL actualizada");
      setCurrentLogo(url.trim());
      setUrl("");
      try {
        const m = await import("@/components/PadelCenterLogo");
        m.invalidatePCLogoCache?.();
      } catch { /* ignore */ }
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <div className="card-tactical p-4 border-[#CCFF00]/30 bg-[#0a1a0a]/40" data-testid="pc-logo-uploader">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} weight="fill" className="text-[#CCFF00]" />
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Logo Padel Center</span>
      </div>
      <div className="flex items-start gap-4 flex-wrap">
        {currentLogo && (
          <img src={currentLogo} alt="Logo actual" className="w-20 h-20 rounded-full object-cover border-2 border-[#CCFF00]/40 shrink-0" data-testid="pc-current-logo" />
        )}
        <div className="flex-1 min-w-[240px] space-y-2">
          <p className="text-xs text-gray-400">Sube un archivo (JPG/PNG/WEBP) o pega una URL pública.</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} className="hidden" data-testid="pc-logo-file-input" />
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="pc-logo-upload-btn">
              {uploading ? "Subiendo..." : "Subir archivo"}
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap pt-1 border-t border-white/5">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="bg-black border-white/15 text-xs flex-1 min-w-[160px]" data-testid="pc-logo-url-input" />
            <Button onClick={setExistingUrl} variant="outline" className="border-white/15 text-xs uppercase" data-testid="pc-logo-url-set">Usar URL</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PadelCenter() {
  const { user } = useAuth();
  const { pc, refresh } = usePadelCenter();
  const [tab, setTab] = useState("ranking");
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = isAdmin && SUPER_ADMIN_EMAILS.includes((user?.email || "").toLowerCase());
  // Only directivos (or admin) get the full ecosystem view. Regular jugadores see only "Mi perfil"
  const hasFullAccess = isAdmin || pc?.role_in_pc === "directivo";

  // Not member and not admin → show join gate
  if (!pc.is_member && !isAdmin) {
    return <JoinGate onJoined={refresh} />;
  }
  // Pending members: locked welcome state
  if (pc.is_member && !isAdmin && pc.status === "pending") {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="pc-pending-state">
        <PadelCenterLogo size={120} glow withText={false} className="mx-auto block" />
        <div className="card-tactical p-6 border-amber-400/40 bg-amber-500/10 text-center max-w-xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-amber-300 mb-2">⏳ Acceso pendiente</h2>
          <p className="text-sm text-gray-300">Tu solicitud fue recibida. El administrador la revisará y te enviaremos un email cuando esté aprobada.</p>
        </div>
      </div>
    );
  }
  if (pc.is_member && !isAdmin && pc.status === "rejected") {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="pc-rejected-state">
        <div className="card-tactical p-6 border-red-500/40 bg-red-500/10 text-center max-w-xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-red-300 mb-2">✗ Acceso denegado</h2>
          <p className="text-sm text-gray-300">Tu acceso al ecosistema Padel Center fue rechazado. Si crees que es un error, contacta al directivo de tu sede.</p>
        </div>
      </div>
    );
  }

  const setVisibility = async (v) => {
    try {
      await api.put("/padel-center/visibility", { visibility: v });
      toast.success("Visibilidad actualizada");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const setSede = async (s) => {
    try {
      await api.put("/padel-center/sede", { sede: s });
      toast.success(s ? "Sede asignada" : "Sede limpiada");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // JUGADORES (no directivos, no admin): solo ven Mi Perfil PC
  if (!hasFullAccess) {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="padel-center-page">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Mi Perfil · Ecosistema Privado</p>
        </div>
        <MyProfileTab pc={pc} onVisibilityChange={setVisibility} onSedeChange={setSede} />
      </div>
    );
  }

  // DIRECTIVOS y ADMIN: vista completa
  return (
    <div className="space-y-5 animate-fade-in" data-testid="padel-center-page">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Ecosistema Privado</p>
        {isAdmin && <PadelCenterBadge size={20} />}
      </div>
      <Welcome clubs={pc.clubs} visibility={pc.visibility} onVisibilityChange={setVisibility} sede={pc.sede} onSedeChange={setSede} />

      {isAdmin && <AdminSetup onSeeded={refresh} />}
      {isSuperAdmin && <AdminLogoUploader onSaved={refresh} />}

      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="pc-tabs">
        <TabsList className={`grid grid-cols-2 ${isSuperAdmin ? "sm:grid-cols-6" : "sm:grid-cols-5"} w-full bg-[#0a0a0a] border border-white/10`}>
          <TabsTrigger value="ranking" data-testid="pc-tab-ranking"><Trophy size={14} className="mr-1" />Ranking</TabsTrigger>
          <TabsTrigger value="dyd" data-testid="pc-tab-dyd"><Coin size={14} className="mr-1" />DYD</TabsTrigger>
          <TabsTrigger value="retas" data-testid="pc-tab-retas"><Sword size={14} className="mr-1" />Retas</TabsTrigger>
          <TabsTrigger value="tournaments" data-testid="pc-tab-tournaments"><ShieldCheck size={14} className="mr-1" />Torneos</TabsTrigger>
          <TabsTrigger value="members" data-testid="pc-tab-members"><Users size={14} className="mr-1" />Miembros</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="code" data-testid="pc-tab-code" className="data-[state=active]:bg-[#CCFF00]/15 data-[state=active]:text-[#CCFF00]"><Key size={14} className="mr-1" />Código</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="ranking" className="mt-4"><RankingTab /></TabsContent>
        <TabsContent value="dyd" className="mt-4"><DydRankingTab /></TabsContent>
        <TabsContent value="retas" className="mt-4"><RetasTab /></TabsContent>
        <TabsContent value="tournaments" className="mt-4"><TournamentsTab /></TabsContent>
        <TabsContent value="members" className="mt-4"><MembersTab isAdmin={isAdmin} onChanged={refresh} /></TabsContent>
        {isSuperAdmin && <TabsContent value="code" className="mt-4"><CodeAdminTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
